/**
 * Drawer menu — language trigger + location/language modal
 */
if (!customElements.get('drawer-localization')) {
  class DrawerLocalization extends HTMLElement {
    constructor() {
      super();
      this.boundDocumentKeydown = this.onDocumentKeydown.bind(this);
    }

    connectedCallback() {
      this.modal = this.querySelector('.drawer-localization__modal');
      this.trigger = this.querySelector('[data-drawer-localization-open]');

      if (this.modal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.form = this.modal?.querySelector('#DrawerLocalizationForm');

      this.trigger?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.open();
      });

      this.modal?.querySelectorAll('[data-drawer-localization-close]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        });
      });

      this.modal?.querySelectorAll('[data-drawer-localization-field-toggle]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleField(button);
        });
      });

      this.modal?.querySelectorAll('[data-drawer-localization-option]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.selectOption(button);
        });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.boundDocumentKeydown);
      document.body.classList.remove('drawer-localization--open');
      this.modal?.remove();
    }

    open() {
      if (!this.modal) return;

      this.closeMenuDrawer();
      this.modal.hidden = false;
      this.trigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('drawer-localization--open');
      document.addEventListener('keydown', this.boundDocumentKeydown);
      this.modal.querySelector('.drawer-localization__close')?.focus();
    }

    closeMenuDrawer() {
      const headerDrawer = this.closest('header-drawer');

      if (headerDrawer?.mainDetailsToggle?.hasAttribute('open')) {
        headerDrawer.dispatchEvent(new CustomEvent('drawer:force-close'));
      }
    }

    close() {
      if (!this.modal) return;

      this.modal.hidden = true;
      this.trigger?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('drawer-localization--open');
      document.removeEventListener('keydown', this.boundDocumentKeydown);
      this.closeAllFields();
      this.trigger?.focus();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    toggleField(button) {
      const field = button.closest('[data-drawer-localization-field]');
      const list = field?.querySelector('.drawer-localization__field-list');
      if (!list) return;

      const isOpen = !list.hidden;

      this.closeAllFields();

      if (!isOpen) {
        list.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        field.classList.add('is-open');
      }
    }

    closeAllFields() {
      this.modal?.querySelectorAll('[data-drawer-localization-field]').forEach((field) => {
        field.classList.remove('is-open');
        field.querySelector('.drawer-localization__field-list')?.setAttribute('hidden', '');
        field.querySelector('[data-drawer-localization-field-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    }

    selectOption(button) {
      const type = button.dataset.drawerLocalizationOption;
      const field = button.closest('[data-drawer-localization-field]');
      const input = field?.querySelector(`[data-drawer-localization-input="${type}"]`);
      const textEl = field?.querySelector(`[data-drawer-localization-field-text="${type}"]`);
      const trigger = field?.querySelector('[data-drawer-localization-field-toggle]');
      const flagEl = trigger?.querySelector('.drawer-localization__flag');

      if (input) input.value = button.dataset.value || '';

      if (textEl) textEl.textContent = button.dataset.label || button.textContent.trim();

      if (flagEl && button.dataset.flag) {
        flagEl.src = button.dataset.flag;
      }

      field?.querySelectorAll('[data-drawer-localization-option]').forEach((option) => {
        option.classList.toggle('is-selected', option === button);
      });

      if (type === 'country' || type === 'language') {
        this.updateTriggerLabel();
      }

      this.closeAllFields();
    }

    updateTriggerLabel() {
      const labelEl = this.querySelector('[data-drawer-localization-trigger-label]');
      const languageInput = this.modal?.querySelector('[data-drawer-localization-input="language"]');
      const countryInput = this.modal?.querySelector('[data-drawer-localization-input="country"]');

      if (!labelEl) return;

      const languageOption = languageInput
        ? this.modal?.querySelector(`[data-drawer-localization-option="language"][data-value="${languageInput.value}"]`)
        : null;
      const countryOption = countryInput
        ? this.modal?.querySelector(`[data-drawer-localization-option="country"][data-value="${countryInput.value}"]`)
        : null;

      const languageLabel = languageOption?.dataset.label
        || labelEl.textContent.split('(')[0].trim();
      const currencyCode = countryOption?.dataset.currency;

      if (!currencyCode) return;

      labelEl.textContent = `${languageLabel} (${currencyCode})`;
    }
  }

  customElements.define('drawer-localization', DrawerLocalization);
}
